import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { CreditCard, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/shadcn-io/spinner'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useBilling } from './billingContext'
import { aiErrorMessage, formatDate, formatMoney, formatTokens, getAIOverview, getAIPurchaseStatus, openBillingPortal, type AIOverview } from '@/lib/ai'

/**
 * Usage, in a dialog that opens from anywhere: how much of this period's
 * tokens are gone, and when they come back.
 *
 * Nothing here decides a balance. Every figure comes from the API, and tokens
 * are only ever credited by the Stripe webhook, so returning from Checkout
 * (which lands with ?checkout=…) polls for the credited state rather than
 * assuming it.
 */
const UsageContent = () => {
    const [searchParams, setSearchParams] = useSearchParams()
    const checkout = searchParams.get('checkout')
    const sessionId = searchParams.get('session_id')
    const pricing = searchParams.get('pricing')
    const { subscription, refreshSubscription, openPricing, balanceVersion } = useBilling()

    const [overview, setOverview] = useState<AIOverview | null>(null)
    const [loading, setLoading] = useState(true)
    const [notice, setNotice] = useState('')

    const loadOverview = useCallback(async () => {
        try {
            setOverview(await getAIOverview())
        } catch (err) {
            toast.error(aiErrorMessage(err))
        } finally {
            setLoading(false)
        }
    }, [])

    // Again whenever the balance may have changed, such as after a plan change.
    useEffect(() => {
        loadOverview()
    }, [loadOverview, balanceVersion])

    const [portalBusy, setPortalBusy] = useState(false)
    const updatePayment = async () => {
        setPortalBusy(true)
        try {
            window.location.href = (await openBillingPortal()).url
        } catch (err) {
            toast.error(aiErrorMessage(err))
            setPortalBusy(false)
        }
    }

    // `?pricing=plans|credits` is the old link to the pricing overlay; it goes to the page now.
    useEffect(() => {
        if (pricing !== 'plans' && pricing !== 'credits') return
        openPricing(pricing)
    }, [pricing, openPricing])

    // Back from Stripe. The webhook is what credits tokens, so poll briefly
    // for the credited state rather than claiming success on the redirect.
    useEffect(() => {
        if (checkout === 'cancelled') {
            toast.info('Checkout cancelled. No payment was taken.')
            searchParams.delete('checkout')
            searchParams.delete('purchase')
            setSearchParams(searchParams, { replace: true })
            return
        }
        if (checkout !== 'success' && checkout !== 'subscribed') return

        let cancelled = false
        let attempts = 0
        setNotice('Payment received. Waiting for your tokens…')
        const clear = () => {
            searchParams.delete('checkout')
            searchParams.delete('session_id')
            setSearchParams(searchParams, { replace: true })
        }
        const poll = async () => {
            attempts += 1
            if (checkout === 'subscribed') {
                const next = await refreshSubscription()
                if (cancelled) return
                if (next?.plan && next.periodAllowance > 0) {
                    setNotice('')
                    toast.success(`You're on ${next.plan.name}. ${formatTokens(next.plan.tokenAmount)} tokens added.`)
                    loadOverview()
                    clear()
                    return
                }
            } else if (sessionId) {
                try {
                    const { purchase } = await getAIPurchaseStatus(sessionId)
                    if (cancelled) return
                    if (purchase?.status === 'credited') {
                        setNotice('')
                        toast.success(`${formatTokens(purchase.tokenAmount + purchase.bonusTokens)} tokens added to your balance.`)
                        loadOverview()
                        clear()
                        return
                    }
                    if (purchase?.status === 'failed' || purchase?.status === 'expired') {
                        setNotice('')
                        toast.error('That payment did not complete. No tokens were added.')
                        // Otherwise reopening Usage would say so again.
                        clear()
                        return
                    }
                } catch {
                    // Keep polling: the row may not be visible yet.
                }
            }
            if (attempts >= 8) {
                setNotice('Payment received. Tokens usually appear within a minute. Reopen Usage shortly to see them.')
                // Reopening loads the balance afresh; there is nothing left to poll for.
                clear()
                return
            }
            setTimeout(poll, 2000)
        }
        poll()
        return () => {
            cancelled = true
        }
    }, [checkout, sessionId, refreshSubscription, loadOverview, searchParams, setSearchParams])

    if (loading) {
        return (
            <div className='flex justify-center py-10'>
                <Spinner variant='circle' />
            </div>
        )
    }

    const balance = overview?.balance
    const available = balance?.availableTokens ?? 0
    const plan = subscription?.plan ?? null
    // What this period gave, against what is left of it. Without a plan the
    // bar measures what was bought rather than an allowance.
    // A plan's period allowance is its tokens; otherwise the balance after the last top-up.
    const allowance = Math.max(plan?.tokenAmount ?? 0, balance?.referenceTokens ?? 0, available) || balance?.lifetimeTokensPurchased || 0
    const used = Math.max(0, allowance - available)
    const percent = allowance > 0 ? Math.min(100, Math.round((used / allowance) * 100)) : 0
    const purchasesEnabled = Boolean(overview?.purchases?.enabled)
    const renews = subscription?.currentPeriodEnd ?? null
    const pastDue = subscription?.status === 'past_due'
    const scheduled = subscription?.scheduledChange ?? null

    return (
        <div className='w-full'>
            {notice && (
                <p className='mt-4 flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900'>
                    <Spinner variant='circle' className='h-4 w-4' /> {notice}
                </p>
            )}

            <section className='mt-2'>
                <div className='flex flex-wrap items-baseline justify-between gap-2'>
                    <div>
                        <p className='text-xs uppercase text-muted-foreground'>Your plan</p>
                        <p className='text-lg font-semibold'>{plan?.name ?? 'No plan'}</p>
                    </div>
                    <p className='text-sm text-muted-foreground'>
                        {allowance > 0 ? (
                            <>
                                <span className='font-medium text-black'>{percent}% used</span> · {formatTokens(available)} of {formatTokens(allowance)} tokens left
                            </>
                        ) : (
                            'No tokens yet'
                        )}
                    </p>
                </div>

                <div className='mt-3 h-2 w-full overflow-hidden rounded-full bg-gray-100'>
                    <div
                        className={`h-full rounded-full transition-all ${percent >= 90 ? 'bg-red-500' : percent >= 70 ? 'bg-amber-500' : 'bg-black'}`}
                        style={{ width: `${allowance > 0 ? percent : 0}%` }}
                    />
                </div>

                <p className='mt-3 text-sm text-muted-foreground'>
                    {plan && renews
                        ? subscription?.cancelAtPeriodEnd
                            ? `Your plan ends on ${formatDate(renews)}. You can use its tokens until then; after that they expire. Top-up tokens stay yours.`
                            : `Resets on ${formatDate(renews)} to ${formatTokens(plan.tokenAmount)} tokens for the new ${plan.billingInterval}. Unused plan tokens expire then and do not carry over; top-up tokens never expire.`
                        : 'Top-up tokens never expire. A plan gives you fresh tokens every billing period, which expire at the end of it.'}
                </p>

                {scheduled && (
                    <p className='mt-3 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900'>
                        From {formatDate(scheduled.from ?? renews)} your plan will be {formatMoney(scheduled.price, scheduled.currency)} for{' '}
                        {formatTokens(scheduled.tokenAmount)} tokens{scheduled.billingInterval ? ` every ${scheduled.billingInterval}` : ''}.
                    </p>
                )}

                {pastDue && (
                    <div className='mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900'>
                        <p>Your last payment did not go through, so this period's tokens are on hold until it does.</p>
                        <Button size='sm' variant='outline' className='mt-2' disabled={portalBusy} onClick={updatePayment}>
                            {portalBusy ? <Spinner variant='circle' className='h-4 w-4' /> : 'Update your payment method'}
                        </Button>
                    </div>
                )}

                {purchasesEnabled && (
                    <div className='mt-4 flex flex-wrap gap-2'>
                        <Button size='sm' disabled={pastDue} onClick={() => openPricing('plans')}>
                            <Sparkles className='h-4 w-4' /> {plan ? 'Change plan' : 'See plans'}
                        </Button>
                        <Button size='sm' variant='outline' onClick={() => openPricing('credits')}>
                            <CreditCard className='h-4 w-4' /> Buy credits
                        </Button>
                    </div>
                )}

                {plan && (
                    <p className='mt-3 text-xs text-muted-foreground'>
                        {formatMoney(plan.price, plan.currency)} every {plan.billingInterval}
                    </p>
                )}
            </section>
        </div>
    )
}

export default function UsageDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className='sm:max-w-lg'>
                <DialogHeader>
                    <DialogTitle>Usage</DialogTitle>
                    <DialogDescription>Your AI tokens for this period.</DialogDescription>
                </DialogHeader>
                {open && <UsageContent />}
            </DialogContent>
        </Dialog>
    )
}
